import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import appSettingsSlice from './Configuration/AppSettings';
import announcementsSlice from './Components/Announcements/AnnouncementSlice';
import { applicationApi } from '../Api/applicationApi';
import applicationSlice from './applicationSlice';
import { triniumApi } from '@api/Trinium/slice';
import { azureMapsApi } from '@api/AzureMaps/slice';
import authorizationSlice from './Security/authorizationSlice';
import toastSlice from './Components/Toast/toastSlice';
import dispatchBoardSlice from '@dispatchBoard/dispatchBoardSlice';
import ediLoadTenderSlice from '@components/DataGrid/EdiLoadTender/ediLoadTenderSlice';
import modalSlice from '@components/Modals/modalSlice';
import formStateSlice from '@/ConsignmentDetails/formStateSlice';

const rootReducers = combineReducers({
	[appSettingsSlice.name]: appSettingsSlice.reducer,
	[authorizationSlice.name]: authorizationSlice.reducer,
	[announcementsSlice.name]: announcementsSlice.reducer,
	[applicationApi.reducerPath]: applicationApi.reducer,
	[applicationSlice.name]: applicationSlice.reducer,
	[dispatchBoardSlice.name]: dispatchBoardSlice.reducer,
	[ediLoadTenderSlice.name]: ediLoadTenderSlice.reducer,
	[formStateSlice.name]: formStateSlice.reducer,
	[modalSlice.name]: modalSlice.reducer,
	[toastSlice.name]: toastSlice.reducer,
	[triniumApi.reducerPath]: triniumApi.reducer,
	[azureMapsApi.reducerPath]: azureMapsApi.reducer
});

export const store = configureStore({
	reducer: rootReducers,
	middleware: (getDefaultMiddleware) => {
		return getDefaultMiddleware({
			serializableCheck: {
				/**
				 * This is disabling the serializable check performed by redux which goes against best practices and should
				 * not be used outside of specific circumstance. In this case, we want to allow callback functions to be passed to the
				 * modal renderer to plugin to the action buttons. Because we are never mutating these value, it is acceptable
				 * in this scenario to store in redux.
				 * 	References:
				 * 		https://redux-toolkit.js.org/usage/usage-guide#working-with-non-serializable-data
				 * 		https://www.bam.tech/article/the-redux-best-practice-do-not-put-non-serializable-values-in-state-or-actions-explained
				 */
				ignoredActions: ['modalSlice/queueModal'],
				ignoredPaths: ['modalSlice.renderQueue']
			}
		}).concat([applicationApi.middleware, triniumApi.middleware, azureMapsApi.middleware]);
	}
});

/**
 * Application specific dispatcher type
 */
export type AppDispatch = typeof store.dispatch;

/**
 * Application specific State structure
 */
export type AppRootState = ReturnType<typeof store.getState>;

/**
 * Hook for dispatching updates to the Redux Store
 * @returns
 */
export const useAppDispatch = () => useDispatch<AppDispatch>();

/**
 * Hook for accessing state from the Redux Store
 */
export const useAppSelector: TypedUseSelectorHook<AppRootState> = useSelector;
